import { ModelBadge } from "@/components/ModelBadge";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  MusicNotes, Waveform, CurrencyDollar, ChartBar, FileText, CreditCard, Trash,
  DownloadSimple, ArrowLeft, Pulse, CheckCircle, Clock, Lightning, Users,
  UserCircle, Crown, Tag, MagnifyingGlass, Funnel, Lock, Eye, EyeSlash, Plus,
  ToggleLeft, ToggleRight, X, Copy, ArrowSquareOut, WarningCircle
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import { toast } from "sonner";

import { API } from "@/lib/apiBase";
const TABS = ["overview", "analytics", "customers", "users", "coupons", "payments", "songs", "activity", "settings"];
const PIE_COLORS = ["#F59E0B", "#10B981", "#8B5CF6", "#EF4444"];

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-[#141417] border border-white/5 hover:border-white/15 transition-all duration-300 rounded-2xl p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(245,158,11,0.15)" }}>
        <Icon size={18} weight="bold" style={{ color: "#F59E0B" }} />
      </div>
      <span className="text-xs uppercase tracking-[0.15em] font-bold" style={{ color: "#A1A1AA" }}>{label}</span>
    </div>
    <p className="text-3xl font-light text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</p>
    {sub && <p className="text-xs mt-1" style={{ color: "#A1A1AA" }}>{sub}</p>}
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState("7d");
  const [users, setUsers] = useState({ items: [], total: 0 });
  const [userSearch, setUserSearch] = useState("");
  const [authFilter, setAuthFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [creditFilter, setCreditFilter] = useState("");
  const [approving, setApproving] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [newCouponCode, setNewCouponCode] = useState("");
  const [newCouponDiscount, setNewCouponDiscount] = useState("");
  const [paymentAnalytics, setPaymentAnalytics] = useState({ successful: [], pending: [] });
  const [retention, setRetention] = useState(null);
  const [lyrics, setLyrics] = useState({ items: [], total: 0 });
  const [songs, setSongs] = useState({ items: [], total: 0 });
  const [payments, setPayments] = useState({ items: [], total: 0 });
  const [activity, setActivity] = useState([]);
  // Customer profiles tab (admin-only enriched view with phone + country + lifetime spend)
  const [customers, setCustomers] = useState({ users: [], count: 0 });
  const [custSearch, setCustSearch] = useState("");
  const [custCountry, setCustCountry] = useState("");
  const [geoAnalytics, setGeoAnalytics] = useState([]);
  const [categoryAnalytics, setCategoryAnalytics] = useState([]);
  const [revenueSplit, setRevenueSplit] = useState({ INR: {}, USD: {} });
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sR, lR, soR, pR, aR, uR, anR, cR, paR, retR] = await Promise.allSettled([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/lyrics`, { withCredentials: true }),
        axios.get(`${API}/admin/songs`, { withCredentials: true }),
        axios.get(`${API}/admin/payments`, { withCredentials: true }),
        axios.get(`${API}/admin/activity`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics?period=${analyticsPeriod}`, { withCredentials: true }),
        axios.get(`${API}/admin/coupons`, { withCredentials: true }),
        axios.get(`${API}/admin/payments/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/retention`, { withCredentials: true }),
      ]);
      if (sR.status === "fulfilled") setStats(sR.value.data);
      if (lR.status === "fulfilled") setLyrics(lR.value.data);
      if (soR.status === "fulfilled") setSongs(soR.value.data);
      if (pR.status === "fulfilled") setPayments(pR.value.data);
      if (aR.status === "fulfilled") setActivity(aR.value.data.activities || []);
      if (uR.status === "fulfilled") setUsers(uR.value.data);
      if (anR.status === "fulfilled") setAnalytics(anR.value.data);
      if (cR.status === "fulfilled") setCoupons(cR.value.data.coupons || []);
      if (paR.status === "fulfilled") setPaymentAnalytics(paR.value.data);
      if (retR.status === "fulfilled") setRetention(retR.value.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [analyticsPeriod]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      if (custSearch) params.set("search", custSearch);
      if (custCountry) params.set("country", custCountry);
      params.set("limit", "200");
      const [customersRes, geoRes, catRes, revRes] = await Promise.all([
        axios.get(`${API}/admin/customers?${params}`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/geography`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/categories`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/revenue-split`, { withCredentials: true }),
      ]);
      setCustomers(customersRes.data);
      setGeoAnalytics(geoRes.data.geography || []);
      setCategoryAnalytics(catRes.data.categories || []);
      setRevenueSplit(revRes.data || { INR: {}, USD: {} });
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (tab === "customers") fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (userSearch) params.set("search", userSearch);
      if (authFilter) params.set("auth_provider", authFilter);
      if (planFilter) params.set("plan", planFilter);
      if (creditFilter) params.set("free_credits_status", creditFilter);
      const res = await axios.get(`${API}/admin/users?${params}`, { withCredentials: true });
      setUsers(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (tab === "users") fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditFilter, authFilter, planFilter]);

  const approveFreeCredits = async (u) => {
    setApproving(u.id);
    try {
      const res = await axios.post(`${API}/admin/users/${u.id}/approve-free-credits`, {}, { withCredentials: true });
      toast.success(`250 credits added to ${u.email}`);
      setUsers((prev) => ({ ...prev, items: prev.items.map((x) => x.id === u.id ? { ...x, credits: res.data.credits, free_credits_status: "approved" } : x) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
    finally { setApproving(""); }
  };

  const fetchAnalytics = async (p) => {
    try {
      const res = await axios.get(`${API}/admin/analytics?period=${p}`, { withCredentials: true });
      setAnalytics(res.data);
    } catch (e) { console.error(e); }
  };

  const createCoupon = async () => {
    if (!newCouponCode || !newCouponDiscount) { toast.error("Enter code and discount"); return; }
    try {
      await axios.post(`${API}/admin/coupons`, { code: newCouponCode, discount: parseFloat(newCouponDiscount) }, { withCredentials: true });
      toast.success("Coupon created!");
      setNewCouponCode(""); setNewCouponDiscount("");
      const res = await axios.get(`${API}/admin/coupons`, { withCredentials: true });
      setCoupons(res.data.coupons || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleCoupon = async (code) => {
    try {
      const res = await axios.put(`${API}/admin/coupons/${code}/toggle`, {}, { withCredentials: true });
      setCoupons(prev => prev.map(c => c.code === code ? { ...c, is_active: res.data.is_active } : c));
      toast.success(`Coupon ${res.data.is_active ? "activated" : "deactivated"}`);
    } catch (e) { toast.error("Failed"); }
  };

  const deleteCoupon = async (code) => {
    try {
      await axios.delete(`${API}/admin/coupons/${code}`, { withCredentials: true });
      setCoupons(prev => prev.filter(c => c.code !== code));
      toast.success("Coupon deleted");
    } catch (e) { toast.error("Failed"); }
  };

  const changePassword = async () => {
    if (!currentPw || !newPw || !confirmPw) { toast.error("Fill all fields"); return; }
    if (newPw !== confirmPw) { toast.error("New passwords don't match"); return; }
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: currentPw, new_password: newPw }, { withCredentials: true });
      toast.success("Password changed!");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (type, id) => {
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, { withCredentials: true });
      toast.success("Deleted"); fetchAll();
    } catch { toast.error("Failed"); }
  };

  const handleExport = (type) => window.open(`${API}/admin/export/${type}`, "_blank");
  const exportPayments = (status) => window.open(`${API}/admin/payments/export-excel?status=${status}`, "_blank");
  const formatDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || "N/A"; } };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#0A0A0C" }}>
      <div className="flex items-center gap-3 text-white">
        <div className="w-6 h-6 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
        <span style={{ fontFamily: "'Manrope', sans-serif" }}>Loading dashboard...</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0C" }}>
      <header className="relative z-10 backdrop-blur-xl bg-[#0A0A0C]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} className="text-white/60 hover:text-white transition-colors" data-testid="admin-back-btn"><ArrowLeft size={24} /></button>
              <div className="flex items-center gap-3">
                <Waveform size={28} weight="bold" style={{ color: "#F59E0B" }} />
                <h1 className="text-xl font-light tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Daremusics <span className="text-white/40">/ Admin</span></h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold" style={{ color: "#F59E0B" }}><ChartBar size={16} weight="fill" /> Dashboard</div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-[#141417] rounded-xl p-1 border border-white/5 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-4 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${tab === t ? "bg-[#F59E0B] text-black" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && stats && (
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard icon={Users} label="Total Users" value={stats.total_users} sub={`+${stats.new_users_7d} this week`} />
              <StatCard icon={Crown} label="Active Subs" value={stats.active_subscriptions} />
              <StatCard icon={CreditCard} label="Paid Customers" value={stats.paid_customers} />
              <StatCard icon={CurrencyDollar} label="Revenue" value={`₹${stats.total_revenue}`} />
            </div>
            {retention && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard icon={Lightning} label="Repeat Customers" value={retention.repeat_customers} />
                <StatCard icon={ChartBar} label="Retention Rate" value={`${retention.retention_rate}%`} />
                <StatCard icon={MusicNotes} label="Total Songs" value={stats.total_songs} />
                <StatCard icon={FileText} label="Total Lyrics" value={stats.total_lyrics} />
              </div>
            )}
            {stats.revenue_by_day && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-[#141417] border border-white/5 rounded-2xl p-6">
                  <h3 className="text-xs uppercase tracking-[0.2em] font-bold mb-6" style={{ color: "#F59E0B" }}>Revenue (7 Days)</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={stats.revenue_by_day}>
                      <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.3} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient></defs>
                      <XAxis dataKey="date" tick={{ fill: "#A1A1AA", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                      <YAxis tick={{ fill: "#A1A1AA", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                      <Tooltip contentStyle={{ background: "#1C1C21", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", color: "#fff" }} />
                      <Area type="monotone" dataKey="revenue" stroke="#F59E0B" fill="url(#rg)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-[#141417] border border-white/5 rounded-2xl p-6">
                  <h3 className="text-xs uppercase tracking-[0.2em] font-bold mb-6" style={{ color: "#F59E0B" }}>Payment Status</h3>
                  {(stats.paid_payments + stats.pending_payments > 0) ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart><Pie data={[{ name: "Paid", value: stats.paid_payments }, { name: "Pending", value: stats.pending_payments }]} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value"><Cell fill="#10B981" /><Cell fill="#F59E0B" /></Pie>
                      <Tooltip contentStyle={{ background: "#1C1C21", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", color: "#fff" }} /></PieChart>
                    </ResponsiveContainer>
                  ) : <div className="flex items-center justify-center h-[220px] text-white/40">No data</div>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ANALYTICS */}
        {tab === "analytics" && (
          <div>
            <div className="flex flex-wrap gap-2 mb-8">
              {[{ id: "today", l: "Today" }, { id: "7d", l: "7 Days" }, { id: "30d", l: "30 Days" }, { id: "3m", l: "3 Months" }, { id: "6m", l: "6 Months" }, { id: "1y", l: "1 Year" }, { id: "3y", l: "3 Years" }].map(p => (
                <button key={p.id} onClick={() => { setAnalyticsPeriod(p.id); fetchAnalytics(p.id); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${analyticsPeriod === p.id ? "bg-[#F59E0B] text-black" : "bg-[#141417] text-white/60 border border-white/5 hover:text-white"}`}>{p.l}</button>
              ))}
            </div>
            {analytics && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <StatCard icon={Users} label="New Users" value={analytics.users_in_period} />
                  <StatCard icon={Crown} label="Paid Customers" value={analytics.paid_customers_period} />
                  <StatCard icon={CurrencyDollar} label="Revenue" value={`₹${analytics.revenue_period}`} />
                  <StatCard icon={MusicNotes} label="Songs" value={analytics.songs_in_period} />
                </div>
                <div className="bg-[#141417] border border-white/5 rounded-2xl p-6 mb-6">
                  <h3 className="text-xs uppercase tracking-[0.2em] font-bold mb-6" style={{ color: "#F59E0B" }}>Revenue Trend</h3>
                  {analytics.chart_data?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={analytics.chart_data}>
                        <defs><linearGradient id="arg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.3} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient></defs>
                        <XAxis dataKey="label" tick={{ fill: "#A1A1AA", fontSize: 10 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} interval="preserveStartEnd" />
                        <YAxis tick={{ fill: "#A1A1AA", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                        <Tooltip contentStyle={{ background: "#1C1C21", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", color: "#fff" }} formatter={(v) => [`₹${v}`, "Revenue"]} />
                        <Area type="monotone" dataKey="revenue" stroke="#F59E0B" fill="url(#arg)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <div className="flex items-center justify-center h-[250px] text-white/40">No data</div>}
                </div>
              </>
            )}
          </div>
        )}

        {/* CUSTOMERS — enriched admin view with phone, country, lifetime spend, geo + category + revenue-split */}
        {tab === "customers" && (
          <div className="space-y-6">
            {/* Revenue split */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard icon={CurrencyDollar} label="Domestic Revenue (INR)" value={`₹${Math.round(revenueSplit?.INR?.total || 0).toLocaleString()}`} sub={`${revenueSplit?.INR?.count || 0} orders`} />
              <StatCard icon={CurrencyDollar} label="International Revenue (USD)" value={`$${Number(revenueSplit?.USD?.total || 0).toFixed(2)}`} sub={`${revenueSplit?.USD?.count || 0} orders`} />
              <StatCard icon={Users} label="Total Customers" value={customers.count} sub="Excluding admins" />
            </div>
            {/* Geography + Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#141417] border border-white/5 rounded-2xl p-5">
                <p className="text-xs uppercase tracking-[0.15em] font-bold mb-3" style={{ color: "#F59E0B" }}>Users by Country</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {geoAnalytics.length === 0 ? <p className="text-xs" style={{ color: "#A1A1AA" }}>No data yet</p> : geoAnalytics.map((g) => (
                    <div key={g.country} className="flex items-center justify-between text-sm">
                      <span className="text-white/80">{g.country}</span>
                      <span className="text-[#F59E0B] font-medium">{g.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#141417] border border-white/5 rounded-2xl p-5">
                <p className="text-xs uppercase tracking-[0.15em] font-bold mb-3" style={{ color: "#F59E0B" }}>Popular Song Categories</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {categoryAnalytics.length === 0 ? <p className="text-xs" style={{ color: "#A1A1AA" }}>No data yet</p> : categoryAnalytics.map((c) => (
                    <div key={c.category} className="flex items-center justify-between text-sm">
                      <span className="text-white/80">{c.category}</span>
                      <span className="text-[#F59E0B] font-medium">{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Search + country filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 bg-[#141417] border border-white/5 rounded-xl focus-within:border-[#F59E0B] flex items-center px-4">
                <MagnifyingGlass size={18} style={{ color: "#A1A1AA" }} />
                <input data-testid="admin-customer-search" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchCustomers()} placeholder="Search name / email / phone..." className="w-full bg-transparent px-3 py-2.5 text-white placeholder:text-gray-500 outline-none text-sm" />
              </div>
              <input data-testid="admin-customer-country" value={custCountry} onChange={(e) => setCustCountry(e.target.value.toUpperCase())} placeholder="Country code (IN, US...)" className="bg-[#141417] border border-white/5 rounded-xl px-4 py-2.5 text-white placeholder:text-gray-500 outline-none text-sm w-44" />
              <button onClick={fetchCustomers} className="bg-[#F59E0B] text-black font-medium rounded-xl px-5 py-2.5 text-sm hover:bg-[#D97706] transition-all"><Funnel size={16} weight="bold" className="inline mr-1" />Filter</button>
            </div>
            {/* Customers table */}
            <div className="bg-[#141417] border border-white/5 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs uppercase tracking-[0.1em]" style={{ color: "#A1A1AA" }}>
                  <tr>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Phone</th>
                    <th className="text-left px-4 py-3">Country</th>
                    <th className="text-right px-4 py-3">Spent (INR)</th>
                    <th className="text-right px-4 py-3">Spent (USD)</th>
                    <th className="text-right px-4 py-3">Txns</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.users.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-white/50">No customers found</td></tr>
                  ) : customers.users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3 text-white">{u.name || "—"}</td>
                      <td className="px-4 py-3 text-white/80">{u.email}</td>
                      <td className="px-4 py-3 text-white/80">{u.phone || "—"}</td>
                      <td className="px-4 py-3 text-white/80">{u.country || "—"}</td>
                      <td className="px-4 py-3 text-right text-[#F59E0B]">₹{Math.round(u.total_spent_inr || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-[#F59E0B]">${Number(u.total_spent_usd || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-white/80">{u.transactions || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* USERS */}
        {tab === "users" && (
          <div>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="flex-1 bg-[#141417] border border-white/5 rounded-xl focus-within:border-[#F59E0B] flex items-center px-4">
                <MagnifyingGlass size={18} style={{ color: "#A1A1AA" }} />
                <input data-testid="admin-user-search" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchUsers()} placeholder="Search by name or email..." className="w-full bg-transparent px-3 py-2.5 text-white placeholder:text-gray-500 outline-none text-sm" />
              </div>
              <select value={authFilter} onChange={(e) => { setAuthFilter(e.target.value); }} className="bg-[#141417] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white outline-none" data-testid="auth-filter">
                <option value="">All Methods</option>
                <option value="google">Google</option>
                <option value="email">Email</option>
              </select>
              <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className="bg-[#141417] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white outline-none" data-testid="plan-filter">
                <option value="">All Plans</option>
                <option value="monthly">Monthly (₹899)</option>
              </select>
              <select value={creditFilter} onChange={(e) => setCreditFilter(e.target.value)} className="bg-[#141417] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white outline-none" data-testid="credit-approval-filter">
                <option value="">All Approvals</option>
                <option value="pending">Pending Approval</option>
                <option value="approved">Approved</option>
              </select>
              <button onClick={fetchUsers} data-testid="admin-users-filter-btn" className="bg-[#F59E0B] text-black font-medium rounded-xl px-5 py-2.5 text-sm hover:bg-[#D97706] transition-all"><Funnel size={16} weight="bold" className="inline mr-1" />Filter</button>
            </div>
            <p className="text-white/40 text-sm mb-4">{users.total} users found</p>
            <div className="space-y-3">
              {users.items.map((u, i) => (
                <div key={u.id || i} className="bg-[#141417] border border-white/5 hover:border-white/15 transition-all rounded-2xl p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: u.auth_provider === "google" ? "rgba(66,133,244,0.15)" : "rgba(245,158,11,0.15)" }}>
                        <UserCircle size={20} weight="bold" style={{ color: u.auth_provider === "google" ? "#4285F4" : "#F59E0B" }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-medium text-sm">{u.name || "N/A"}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-white/40 text-xs">{u.email}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${u.auth_provider === "google" ? "bg-blue-500/10 text-blue-400" : "bg-yellow-500/10 text-yellow-400"}`}>{u.auth_provider === "google" ? "Google" : "Email"}</span>
                          {u.subscription && <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">{u.subscription.plan_name}</span>}
                          {u.role !== "admin" && <span className="text-xs px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B]" data-testid={`user-credits-${u.id}`}>{u.credits ?? 0} credits</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {u.role !== "admin" && (u.free_credits_status === "approved" ? (
                        <span className="text-xs px-3 py-1.5 rounded-full bg-green-500/10 text-green-400" data-testid={`approved-badge-${u.id}`}>Approved</span>
                      ) : (
                        <button onClick={() => approveFreeCredits(u)} disabled={approving === u.id} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#F59E0B] text-black hover:bg-[#D97706] transition-colors disabled:opacity-50" data-testid={`approve-credits-btn-${u.id}`}>{approving === u.id ? "..." : "Approve +250"}</button>
                      ))}
                      <span className="text-white/30 text-xs">{formatDate(u.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* COUPONS */}
        {tab === "coupons" && (
          <div>
            <div className="bg-[#141417] border border-white/5 rounded-2xl p-6 mb-6">
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold mb-4" style={{ color: "#F59E0B" }}>Create Coupon</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input data-testid="coupon-code-input" value={newCouponCode} onChange={(e) => setNewCouponCode(e.target.value.toUpperCase())} placeholder="COUPON CODE" className="flex-1 bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-500 outline-none text-sm uppercase" />
                <input data-testid="coupon-discount-input" type="number" value={newCouponDiscount} onChange={(e) => setNewCouponDiscount(e.target.value)} placeholder="Discount (₹)" className="w-40 bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-500 outline-none text-sm" />
                <button onClick={createCoupon} className="bg-[#F59E0B] text-black font-semibold rounded-xl px-6 py-3 text-sm hover:bg-[#D97706] transition-all flex items-center gap-2" data-testid="create-coupon-btn"><Plus size={16} weight="bold" /> Create</button>
              </div>
            </div>
            <div className="space-y-3">
              {coupons.length === 0 ? <div className="bg-[#141417] border border-white/5 rounded-2xl p-8 text-center text-white/40">No coupons yet</div> :
                coupons.map((c) => (
                  <div key={c.code} className="bg-[#141417] border border-white/5 hover:border-white/15 transition-all rounded-2xl p-5" data-testid={`coupon-${c.code}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1">
                        <Tag size={20} style={{ color: "#F59E0B" }} />
                        <div>
                          <p className="text-white font-bold text-sm">{c.code}</p>
                          <p className="text-white/40 text-xs">₹{c.discount} off &middot; Used by {c.used_by?.length || 0} users</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => toggleCoupon(c.code)} className="transition-colors" data-testid={`toggle-${c.code}`}>
                          {c.is_active ? <ToggleRight size={28} weight="fill" style={{ color: "#10B981" }} /> : <ToggleLeft size={28} weight="fill" style={{ color: "#A1A1AA" }} />}
                        </button>
                        <button onClick={() => deleteCoupon(c.code)} className="text-white/30 hover:text-red-400 transition-colors"><Trash size={18} /></button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* PAYMENTS */}
        {tab === "payments" && (
          <div>
            <div className="flex gap-3 mb-6">
              <button onClick={() => exportPayments("successful")} className="flex items-center gap-2 bg-green-500/10 text-green-400 border border-green-500/20 font-medium rounded-full px-5 py-2 text-sm hover:bg-green-500/20 transition-all" data-testid="export-successful"><DownloadSimple size={16} /> Export Successful (CSV)</button>
              <button onClick={() => exportPayments("pending")} className="flex items-center gap-2 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 font-medium rounded-full px-5 py-2 text-sm hover:bg-yellow-500/20 transition-all" data-testid="export-pending"><DownloadSimple size={16} /> Export Pending (CSV)</button>
            </div>
            <h3 className="text-lg text-white font-medium mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Successful Payments ({paymentAnalytics.successful.length})</h3>
            <div className="space-y-2 mb-8">
              {paymentAnalytics.successful.slice(0, 20).map((p, i) => (
                <div key={p.order_id || i} className="bg-[#141417] border border-white/5 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <CheckCircle size={18} weight="fill" style={{ color: "#10B981" }} />
                    <div className="min-w-0"><p className="text-white text-sm">₹{p.amount} — {p.plan_id || "N/A"}</p><p className="text-white/30 text-xs truncate">{p.user_id}</p></div>
                  </div>
                  <span className="text-white/30 text-xs shrink-0">{formatDate(p.created_at)}</span>
                </div>
              ))}
              {paymentAnalytics.successful.length === 0 && <div className="bg-[#141417] border border-white/5 rounded-xl p-6 text-center text-white/40">No successful payments</div>}
            </div>
            <h3 className="text-lg text-white font-medium mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Pending/Initiated ({paymentAnalytics.pending.length})</h3>
            <div className="space-y-2">
              {paymentAnalytics.pending.slice(0, 20).map((p, i) => (
                <div key={p.order_id || i} className="bg-[#141417] border border-white/5 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Clock size={18} weight="bold" style={{ color: "#F59E0B" }} />
                    <div className="min-w-0"><p className="text-white text-sm">₹{p.amount} — {p.plan_id || "N/A"}</p><p className="text-white/30 text-xs truncate">{p.user_id}</p></div>
                  </div>
                  <span className="text-white/30 text-xs shrink-0">{formatDate(p.created_at)}</span>
                </div>
              ))}
              {paymentAnalytics.pending.length === 0 && <div className="bg-[#141417] border border-white/5 rounded-xl p-6 text-center text-white/40">No pending payments</div>}
            </div>
          </div>
        )}

        {/* SONGS */}
        {tab === "songs" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl text-white font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>All Songs ({songs.total})</h3>
              <button onClick={() => handleExport("songs")} className="flex items-center gap-2 bg-transparent text-white border border-white/20 font-medium rounded-full px-5 py-2 text-sm hover:bg-white/5 transition-all"><DownloadSimple size={16} /> Export CSV</button>
            </div>
            <p className="text-xs text-white/40 mb-4">Inline player + direct download — use these to verify a customer's song or send the MP3 manually via WhatsApp/Email when the user can't access it on their end.</p>
            <div className="space-y-4">
              {songs.items.map(s => {
                const audioUrl = s.song_url || s.stream_url || "";
                const statusColors = { completed: "bg-green-500/10 text-green-400", processing: "bg-yellow-500/10 text-yellow-400", failed: "bg-red-500/10 text-red-400" };
                const statusCls = statusColors[s.status] || "bg-white/10 text-white/60";
                return (
                  <div key={s.id} data-testid={`admin-song-row-${s.id}`} className="bg-[#141417] border border-white/5 hover:border-white/15 transition-all rounded-2xl p-5">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <MusicNotes size={18} weight="fill" style={{ color: "#F59E0B" }} className="mt-1 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-white text-sm font-medium truncate">{s.song_name || s.id}</p>
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${statusCls}`}>{s.status || "unknown"}</span>
                            <ModelBadge song={s} />
                            {s.is_trial && !s.is_unlocked && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold bg-orange-500/10 text-orange-400">Trial</span>}
                          </div>
                          <p className="text-white/40 text-xs mt-1 truncate">
                            <span className="text-white/60">{s.user_email || s.user_id}</span>
                            {s.user_name && <span className="text-white/30"> · {s.user_name}</span>}
                            <span className="text-white/30"> · {formatDate(s.created_at)}</span>
                          </p>
                        </div>
                      </div>
                      <button onClick={() => handleDelete("songs", s.id)} className="text-white/30 hover:text-red-400 transition-colors shrink-0" title="Delete song"><Trash size={18} /></button>
                    </div>

                    {audioUrl ? (
                      <>
                        <audio data-testid={`admin-audio-${s.id}`} controls preload="none" src={`${API}/songs/stream/${s.id}`} className="w-full rounded-lg" style={{ filter: "invert(0.9) hue-rotate(180deg)" }} />
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <a
                            href={`${API}/admin/songs/${s.id}/download`}
                            className="inline-flex items-center gap-1.5 bg-[#F59E0B] text-black font-semibold rounded-full px-4 py-1.5 text-xs hover:bg-[#D97706] transition-all"
                            data-testid={`admin-download-${s.id}`}
                          >
                            <DownloadSimple size={14} weight="bold" /> Download MP3
                          </a>
                          <a
                            href={audioUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 bg-transparent text-white border border-white/20 font-medium rounded-full px-4 py-1.5 text-xs hover:bg-white/5 transition-all"
                            data-testid={`admin-open-url-${s.id}`}
                          >
                            <ArrowSquareOut size={14} weight="bold" /> Open Source URL
                          </a>
                          <button
                            onClick={() => { navigator.clipboard.writeText(audioUrl); toast.success("Source URL copied"); }}
                            className="inline-flex items-center gap-1.5 bg-transparent text-white border border-white/20 font-medium rounded-full px-4 py-1.5 text-xs hover:bg-white/5 transition-all"
                            data-testid={`admin-copy-url-${s.id}`}
                          >
                            <Copy size={14} weight="bold" /> Copy URL
                          </button>
                        </div>
                        <p className="mt-2 text-[11px] text-white/40 break-all font-mono leading-snug" data-testid={`admin-song-url-${s.id}`}>{audioUrl}</p>
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-orange-400/90 bg-orange-500/5 border border-orange-500/20 rounded-lg px-3 py-2">
                        <WarningCircle size={14} weight="fill" /> No audio URL available (song may still be processing or generation failed)
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* LYRICS */}
        {tab === "lyrics" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl text-white font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>All Lyrics ({lyrics.total})</h3>
              <button onClick={() => handleExport("lyrics")} className="flex items-center gap-2 bg-transparent text-white border border-white/20 font-medium rounded-full px-5 py-2 text-sm hover:bg-white/5 transition-all"><DownloadSimple size={16} /> Export CSV</button>
            </div>
            <div className="space-y-3">
              {lyrics.items.map(l => (
                <div key={l.id} className="bg-[#141417] border border-white/5 rounded-2xl p-5 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0"><p className="text-white font-medium mb-1 truncate">{l.concept}</p><p className="text-white/60 text-sm line-clamp-2">{l.lyrics}</p><span className="text-white/30 text-xs">{formatDate(l.created_at)}</span></div>
                  <button onClick={() => handleDelete("lyrics", l.id)} className="text-white/30 hover:text-red-400 transition-colors shrink-0"><Trash size={18} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ACTIVITY */}
        {tab === "activity" && (
          <div>
            <h3 className="text-xl text-white font-medium mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Recent Activity</h3>
            <div className="space-y-2">
              {activity.map((a, i) => (
                <div key={`${a.type}-${a.id}-${i}`} className="flex items-center gap-4 bg-[#141417] border border-white/5 rounded-xl px-5 py-3.5 hover:border-white/10 transition-all">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 shrink-0">
                    {a.type === "lyrics" ? <FileText size={16} weight="bold" style={{ color: "#F59E0B" }} /> : a.type === "song" ? <MusicNotes size={16} weight="bold" style={{ color: "#10B981" }} /> : <CreditCard size={16} weight="bold" style={{ color: "#8B5CF6" }} />}
                  </div>
                  <p className="flex-1 text-white/80 text-sm truncate">{a.description}</p>
                  <span className="text-white/30 text-xs shrink-0">{formatDate(a.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {tab === "settings" && (
          <div className="max-w-md">
            <h3 className="text-xl text-white font-medium mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Admin Settings</h3>
            <div className="bg-[#141417] border border-white/5 rounded-2xl p-6">
              <h4 className="text-xs uppercase tracking-[0.2em] font-bold mb-4" style={{ color: "#F59E0B" }}>Change Password</h4>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-[#A1A1AA] block mb-1">Current Password</label>
                  <div className="bg-[#0A0A0C] border border-white/10 rounded-xl flex items-center px-4">
                    <Lock size={16} style={{ color: "#A1A1AA" }} />
                    <input type={showPw ? "text" : "password"} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="w-full bg-transparent py-3 px-3 text-white outline-none text-sm" data-testid="current-password" />
                    <button onClick={() => setShowPw(!showPw)} className="text-white/30 hover:text-white">{showPw ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#A1A1AA] block mb-1">New Password</label>
                  <div className="bg-[#0A0A0C] border border-white/10 rounded-xl flex items-center px-4">
                    <Lock size={16} style={{ color: "#A1A1AA" }} />
                    <input type={showPw ? "text" : "password"} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="w-full bg-transparent py-3 px-3 text-white outline-none text-sm" placeholder="Min 6 characters" data-testid="new-password" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#A1A1AA] block mb-1">Confirm New Password</label>
                  <div className="bg-[#0A0A0C] border border-white/10 rounded-xl flex items-center px-4">
                    <Lock size={16} style={{ color: "#A1A1AA" }} />
                    <input type={showPw ? "text" : "password"} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="w-full bg-transparent py-3 px-3 text-white outline-none text-sm" placeholder="Confirm password" data-testid="confirm-password" />
                  </div>
                </div>
                <button onClick={changePassword} className="bg-[#F59E0B] text-black font-semibold rounded-full px-8 py-3 hover:bg-[#D97706] transition-all w-full text-sm" data-testid="change-password-btn">Update Password</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
